import { Item, StoreDetails, DeliveryCharge, Discount } from '../types';

interface TelegramConfig {
  botToken: string;
  chatId: string;
  enabled: boolean;
}

/**
 * Load Telegram configuration from local storage
 */
export const loadTelegramConfig = (): TelegramConfig => {
  const savedConfig = localStorage.getItem('telegramConfig');
  if (savedConfig) {
    return JSON.parse(savedConfig);
  }
  return {
    botToken: '',
    chatId: '',
    enabled: false
  };
};

/**
 * Save Telegram configuration to local storage
 */
export const saveTelegramConfig = (config: TelegramConfig): void => {
  localStorage.setItem('telegramConfig', JSON.stringify(config));
};

/**
 * Format receipt content as text for Telegram message
 */
export const formatReceiptForTelegram = (
  items: Item[],
  storeDetails: StoreDetails,
  deliveryCharge: DeliveryCharge,
  discount: Discount
): string => {
  // Store header
  let message = `*${storeDetails.name}*\n`;
  message += `${storeDetails.address}\n`;
  message += `${storeDetails.phone}\n`;
  
  if (storeDetails.email) {
    message += `${storeDetails.email}\n`;
  }
  
  if (storeDetails.website) {
    message += `${storeDetails.website}\n`;
  }
  
  // Date and time
  const now = new Date();
  message += `\nDate: ${now.toLocaleDateString()}\n`;
  message += `Time: ${now.toLocaleTimeString()}\n`;
  message += '------------------------------------------------\n\n';
  
  // Items
  let subtotal = 0;
  
  items.forEach(item => {
    const itemTotal = item.quantity * item.price;
    subtotal += itemTotal;
    
    // Item name and details
    message += `*${item.name}*\n`;
    message += `  ${item.quantity} x Tk ${item.price} = Tk ${itemTotal}\n\n`;
  });
  
  // Totals
  message += '------------------------------------------------\n';
  message += `Subtotal: Tk ${subtotal}\n`;
  
  // Discount
  if (discount.amount > 0) {
    message += `${discount.label}: Tk ${discount.amount}\n`;
  }
  
  // Discounted subtotal
  const discountedSubtotal = subtotal - discount.amount;
  
  // Delivery charge
  if (deliveryCharge.amount > 0) {
    message += `${deliveryCharge.label}: Tk ${deliveryCharge.amount}\n`;
  }
  
  // Total
  const total = discountedSubtotal + deliveryCharge.amount;
  message += `\n*TOTAL: Tk ${total}*\n\n`;
  
  // Footer
  message += '------------------------------------------------\n';
  message += 'Thank you for your purchase!\n';
  message += 'Visit us again soon!';
  
  return message;
};

/**
 * Send receipt to Telegram
 */
export const sendReceiptToTelegram = async (
  items: Item[],
  storeDetails: StoreDetails,
  deliveryCharge: DeliveryCharge,
  discount: Discount
): Promise<{ success: boolean; message: string }> => {
  try {
    const config = loadTelegramConfig();
    
    // Check if Telegram integration is enabled and configured
    if (!config.enabled || !config.botToken || !config.chatId) {
      return { 
        success: false, 
        message: 'Telegram integration is not enabled or properly configured' 
      };
    }
    
    // Format receipt content
    const messageText = formatReceiptForTelegram(items, storeDetails, deliveryCharge, discount);
    
    // Send message to Telegram
    const response = await fetch(`https://api.telegram.org/bot${config.botToken}/sendMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: config.chatId,
        text: messageText,
        parse_mode: 'Markdown'
      })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return { 
        success: false, 
        message: `Failed to send message: ${data.description || 'Unknown error'}` 
      };
    }
    
    return { success: true, message: 'Receipt sent to Telegram successfully' };
  } catch (error) {
    console.error('Error sending receipt to Telegram:', error);
    return { 
      success: false, 
      message: `Error sending receipt: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
};
